import React, { useId, useMemo, useRef, useState } from 'react';
import Modal, { useModalClose } from './Modal';
import { plural } from '../utils/format';

/**
 * <ImportCsvModal /> — reusable bulk CSV import dialog (Re-flow 'Import' button parity).
 * Pick a .csv → parse client-side → auto-map headers to fields (each mapping can be
 * changed by hand) → preview → import, with progress and a result summary.
 * @param {string} title
 * @param {Array<{key:string,label:string,required?:boolean}>} fields
 * @param {(rows:Object[], opts)=>void|Promise} onImport  rows are objects keyed by field.key (see contract below)
 * @param {()=>void} onClose
 */

/* Tiny CSV parser: quoted fields may contain commas/newlines; '""' unescapes to '"'.
   Rows split on \r?\n (or lone \r) outside quotes; first row = headers. */
const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cell += '"'; i += 1; } else inQuotes = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell); cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(cell); rows.push(row); row = []; cell = '';
    } else {
      cell += ch;
    }
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((c) => String(c).trim() !== ''));
};

/* 'Job No.' / 'job_no' / 'JOBNO' all normalise to 'jobno' for header matching. */
const normKey = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const NOT_IMPORTED = -1;

/**
 * <ImportCsvModal /> contract:
 *   onImport(rows, { onProgress(done, total) }) may be async. The dialog stays open
 *   while it runs (progress shown when onProgress is called) and then shows a result.
 *   Resolve with { created?, updated?, failed?, skipped?: [{ row, reason }], message? }
 *   to get a result summary in the dialog; resolve with nothing and the dialog closes
 *   (the caller reports the outcome itself). Throwing keeps the dialog open with the
 *   error, so the file and mapping survive a retry.
 */
const ImportCsvModal = ({ title, fields, onImport, onClose }) => {
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [error, setError] = useState('');
  const [overrides, setOverrides] = useState({});     // field.key → header index chosen by hand
  const [phase, setPhase] = useState('pick');          // pick | importing | done
  const [progress, setProgress] = useState(null);      // { done, total }
  const [result, setResult] = useState(null);
  const [importError, setImportError] = useState(null);
  const fileRef = useRef(null);
  const ids = useId().replace(/:/g, '');

  // Auto-map each field to a header index (case-insensitive, punctuation-insensitive,
  // matched against both field.key and field.label), then apply manual choices.
  const mapping = useMemo(() => {
    const m = {};
    fields.forEach((f) => {
      m[f.key] = f.key in overrides ? overrides[f.key] : headers.findIndex((h) => {
        const n = normKey(h);
        return n && (n === normKey(f.key) || n === normKey(f.label));
      });
    });
    return m;
  }, [fields, headers, overrides]);

  // Data rows → objects keyed by field.key, dropping rows blank on every mapped field.
  const rows = useMemo(() => dataRows
    .map((r) => {
      const obj = {};
      fields.forEach((f) => {
        const idx = mapping[f.key];
        obj[f.key] = idx >= 0 ? String(r[idx] ?? '').trim() : '';
      });
      return obj;
    })
    .filter((obj) => fields.some((f) => obj[f.key] !== '')), [dataRows, fields, mapping]);

  const mappedFields = fields.filter((f) => mapping[f.key] >= 0);
  const missingRequired = fields.filter((f) => f.required && mapping[f.key] < 0);
  const canImport = Boolean(fileName) && !error && !missingRequired.length && rows.length > 0 && phase === 'pick';
  const busy = phase === 'importing';

  const readFile = (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name) && !/csv|text\/plain|excel/i.test(file.type || '')) {
      setFileName(file.name); setError('That isn’t a CSV file — in Excel use File → Save As → CSV.'); setHeaders([]); setDataRows([]);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '').replace(/^﻿/, '');   // strip Excel's BOM
      const parsed = parseCsv(text);
      setFileName(file.name);
      setOverrides({});
      setImportError(null);
      if (parsed.length < 2) {
        setError('That CSV has no data rows — the first row must be column headers.');
        setHeaders([]); setDataRows([]);
        return;
      }
      setError('');
      setHeaders(parsed[0].map((h) => String(h).trim()));
      setDataRows(parsed.slice(1));
    };
    reader.onerror = () => { setFileName(file.name); setError('Couldn’t read that file.'); setHeaders([]); setDataRows([]); };
    reader.readAsText(file);
  };
  const onFile = (e) => { readFile(e.target.files && e.target.files[0]); e.target.value = ''; };
  const onDrop = (e) => { e.preventDefault(); readFile(e.dataTransfer?.files?.[0]); };

  // Sample CSV: header row = field labels, plus one example row. BOM so Excel reads £ etc.
  const downloadSample = () => {
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = [
      fields.map((f) => esc(f.label)).join(','),
      fields.map((f) => esc(`Example ${f.label.toLowerCase()}`)).join(','),
    ];
    const blob = new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'import-sample.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const doImport = async () => {
    if (!canImport) return;
    setPhase('importing'); setImportError(null); setProgress({ done: 0, total: rows.length });
    try {
      const r = await onImport(rows, { onProgress: (done, total) => setProgress({ done, total: total ?? rows.length }) });
      if (r && typeof r === 'object') { setResult(r); setPhase('done'); }
      else onClose();
    } catch (e) {
      setImportError((e && (e.body?.message || e.message)) || 'The import failed — nothing was confirmed. Try again.');
      setPhase('pick');
    }
  };

  const pct = progress && progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <Modal
      title={title}
      maxWidth={680}
      dirty={(Boolean(fileName) && phase === 'pick') || busy}
      closeOnBackdrop={!busy}
      hideClose={busy}
      onClose={() => { if (!busy) onClose(); }}
      footer={phase === 'done' ? (
        <button type="button" className="cw-btn cw-btn-primary" onClick={onClose}>Done</button>
      ) : (<>
        <CancelButton disabled={busy} />
        <button type="button" className="cw-btn cw-btn-primary" onClick={doImport} disabled={!canImport} aria-busy={busy || undefined}>
          <i className={`fa-solid ${busy ? 'fa-spinner fa-spin' : 'fa-file-import'}`} aria-hidden="true" /> {busy ? 'Importing…' : `Import ${rows.length ? plural(rows.length, 'row') : ''}`.trim()}
        </button>
      </>)}
    >
      {phase === 'done' && result ? (
        <ImportResult result={result} />
      ) : (
        <>
          {busy && (
            <div role="status" aria-live="polite" style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 'var(--cs-text-sm)', fontWeight: 600, marginBottom: 6 }}>
                Importing… {progress ? `${progress.done} of ${progress.total}` : ''} — keep this open until it finishes.
              </div>
              <div style={{ height: 8, background: 'var(--cw-bg)', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: 'var(--cw-blue)', transition: 'width .2s' }} />
              </div>
            </div>
          )}
          {importError && (
            <div className="cw-error-state compact" role="alert" style={{ marginBottom: 14 }}>
              <div className="cw-error-state-text"><p className="cw-error-state-title">Import failed</p><p className="cw-error-state-reason">{importError}</p></div>
            </div>
          )}

          <div className="cw-field">
            <span className="cw-label" id={`${ids}-file`}>CSV file</span>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, border: '1px dashed var(--cw-border-strong)', borderRadius: 10, padding: 18, color: 'var(--cw-text-muted)', fontSize: 'var(--cs-text-sm)', textAlign: 'center' }}
            >
              <i className="fa-solid fa-file-csv" style={{ fontSize: '1.4rem', color: 'var(--cw-placeholder)' }} aria-hidden="true" />
              {fileName
                ? <span style={{ color: 'var(--cw-success-fg)', fontWeight: 600 }}>{fileName}</span>
                : <span>Drop a .csv here, or choose one — the first row must be column headers</span>}
              <button type="button" className="cw-btn cw-btn-secondary" onClick={() => fileRef.current?.click()} disabled={busy} aria-describedby={`${ids}-file`}>
                <i className="fa-solid fa-folder-open" aria-hidden="true" /> {fileName ? 'Choose a different file' : 'Choose CSV file'}
              </button>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="cw-sr-only" tabIndex={-1} aria-hidden="true" onChange={onFile} />
            </div>
            <button type="button" className="cw-btn-link" onClick={downloadSample} style={{ marginTop: 6, alignSelf: 'flex-start' }}>
              <i className="fa-solid fa-download" style={{ marginRight: 4 }} aria-hidden="true" /> Download sample CSV
            </button>
          </div>

          {error && (
            <div role="alert" style={{ fontSize: 'var(--cs-text-sm)', color: 'var(--cw-danger-fg)', background: 'var(--cw-danger-tint)', border: '1px solid var(--cw-danger-border)', borderRadius: 8, padding: '8px 12px', margin: '12px 0 14px' }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} aria-hidden="true" /> {error}
            </div>
          )}

          {headers.length > 0 && (
            <>
              <fieldset style={{ border: 'none', padding: 0, margin: '14px 0 0' }} disabled={busy}>
                <legend className="cw-label" style={{ marginBottom: 6 }}>Column mapping — check each field reads from the right column</legend>
                <div style={{ border: '1px solid var(--cw-border)', borderRadius: 10, overflow: 'hidden' }}>
                  {fields.map((f, i) => {
                    const idx = mapping[f.key];
                    const selId = `${ids}-map-${i}`;
                    return (
                      <div key={f.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '7px 12px', borderTop: i ? '1px solid var(--cw-border-light)' : 'none', flexWrap: 'wrap' }}>
                        <label htmlFor={selId} style={{ fontWeight: 600, color: 'var(--cw-text)', fontSize: 'var(--cs-text-sm)', flex: '1 1 160px' }}>
                          {f.label}{f.required && <span className="cw-req" aria-hidden="true"> *</span>}{f.required && <span className="cw-sr-only"> (required)</span>}
                        </label>
                        <select id={selId} className="cw-input" style={{ flex: '1 1 200px', maxWidth: 280 }} value={idx}
                          aria-invalid={f.required && idx < 0 ? true : undefined}
                          onChange={(e) => setOverrides((o) => ({ ...o, [f.key]: Number(e.target.value) }))}>
                          <option value={NOT_IMPORTED}>{f.required ? 'Choose a column…' : 'Not imported'}</option>
                          {headers.map((h, hi) => <option key={hi} value={hi}>{h || `Column ${hi + 1}`}</option>)}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </fieldset>

              {mappedFields.length > 0 && rows.length > 0 && (
                <div className="cw-field" style={{ marginTop: 14 }}>
                  <span className="cw-label">Preview (first {Math.min(5, rows.length)} of {rows.length})</span>
                  <div style={{ overflowX: 'auto', border: '1px solid var(--cw-border)', borderRadius: 10 }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--cs-text-sm)' }}>
                      <caption className="cw-sr-only">Preview of the first rows to import</caption>
                      <thead>
                        <tr>
                          {mappedFields.map((f) => (
                            <th scope="col" key={f.key} style={{ textAlign: 'left', padding: '7px 10px', background: 'var(--cw-surface-alt)', color: 'var(--cw-text-muted)', fontWeight: 700, whiteSpace: 'nowrap' }}>{f.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.slice(0, 5).map((r, i) => (
                          <tr key={i}>
                            {mappedFields.map((f) => (
                              <td key={f.key} style={{ padding: '7px 10px', borderTop: '1px solid var(--cw-border-light)', color: 'var(--cw-text-2)', whiteSpace: 'nowrap' }}>{r[f.key] || '—'}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {missingRequired.length > 0 ? (
                <div role="alert" style={{ fontSize: 'var(--cs-text-sm)', color: 'var(--cw-danger-fg)', marginTop: 10 }}>
                  <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} aria-hidden="true" />
                  Choose a column for: {missingRequired.map((f) => f.label).join(', ')}.
                </div>
              ) : (
                <div style={{ fontSize: 'var(--cs-text-sm)', color: 'var(--cw-success-fg)', fontWeight: 600, marginTop: 10 }}>
                  <i className="fa-solid fa-circle-check" style={{ marginRight: 6 }} aria-hidden="true" />
                  {plural(rows.length, 'row')} ready to import
                </div>
              )}
            </>
          )}
        </>
      )}
    </Modal>
  );
};

/** Outcome summary: "3 of 5 imported — 2 failed", plus why. */
const ImportResult = ({ result }) => {
  const created = Number(result.created || 0);
  const updated = Number(result.updated || 0);
  const skipped = Array.isArray(result.skipped) ? result.skipped : [];
  const failedCount = Number(result.failed || 0);
  const total = result.total != null ? Number(result.total) : created + updated + skipped.length + failedCount;
  const ok = created + updated;
  const bad = total - ok;
  const tone = bad === 0 ? 'success' : ok === 0 ? 'danger' : 'warning';
  const fg = { success: 'var(--cw-success-fg)', warning: 'var(--cw-warning-fg)', danger: 'var(--cw-danger-fg)' }[tone];
  const icon = { success: 'fa-circle-check', warning: 'fa-triangle-exclamation', danger: 'fa-circle-xmark' }[tone];
  return (
    <div role="status" style={{ display: 'grid', gap: 12 }}>
      <p style={{ margin: 0, fontWeight: 700, color: fg, fontSize: 'var(--cs-text-base)' }}>
        <i className={`fa-solid ${icon}`} style={{ marginRight: 6 }} aria-hidden="true" />
        {result.message || (bad === 0 ? `All ${plural(total, 'row')} imported` : `${ok} of ${total} imported — ${bad} not imported`)}
      </p>
      {(created > 0 || updated > 0) && (
        <p className="cw-meta" style={{ margin: 0 }}>{[created ? `${created} added` : null, updated ? `${updated} updated` : null].filter(Boolean).join(' · ')}</p>
      )}
      {skipped.length > 0 && (
        <div style={{ border: '1px solid var(--cw-border)', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '8px 12px', background: 'var(--cw-surface-alt)', borderBottom: '1px solid var(--cw-border)', fontWeight: 700, fontSize: 'var(--cs-text-sm)' }}>Rows not imported</div>
          <ul style={{ margin: 0, padding: '8px 12px 8px 28px', maxHeight: 220, overflow: 'auto', fontSize: 'var(--cs-text-sm)' }}>
            {skipped.map((sk, i) => <li key={i}><strong>{sk.row || `Row ${i + 1}`}</strong> — {sk.reason || 'not imported'}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

const CancelButton = ({ disabled }) => {
  const requestClose = useModalClose();
  return <button type="button" className="cw-btn cw-btn-secondary" onClick={requestClose} disabled={disabled}>Cancel</button>;
};

export default ImportCsvModal;
